import React, { useState, useEffect } from 'react';
import { useFormBuilder } from '../../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Search, Plus, MoreVertical, Edit, Trash2, Copy, Eye, ExternalLink,
  FileText, RefreshCw, Play, Pause, CheckCircle, Link2
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'DRAFT': 'bg-amber-100 text-amber-700',
  'LIVE': 'bg-green-100 text-green-700',
  'CLOSED': 'bg-slate-100 text-slate-700'
};

const FormList = ({ onEdit, onViewResponses, onCreateNew }) => {
  const { 
    forms, loading, fetchForms, deleteForm, cloneForm, updateFormStatus 
  } = useFormBuilder();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, form: null });
  const [linkDialog, setLinkDialog] = useState({ open: false, form: null });

  useEffect(() => {
    fetchForms();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchForms({ 
        search: search || undefined, 
        status: statusFilter === 'all' ? undefined : statusFilter 
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  const handleDelete = async () => {
    if (!deleteDialog.form) return;
    const result = await deleteForm(deleteDialog.form.id);
    if (result.success) {
      toast.success('Form deleted successfully');
    } else {
      toast.error(result.error);
    }
    setDeleteDialog({ open: false, form: null });
  };

  const handleClone = async (form) => {
    const result = await cloneForm(form.id);
    if (result.success) {
      toast.success('Form cloned successfully');
      onEdit(result.data.id);
    } else {
      toast.error(result.error);
    }
  };

  const handleStatusChange = async (form, newStatus) => {
    const result = await updateFormStatus(form.id, newStatus);
    if (result.success) {
      toast.success(`Form status changed to ${newStatus}`);
    } else {
      toast.error(result.error);
    }
  };

  const getPublicUrl = (form) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/f/${form.public_id}`;
  };

  const copyLink = (form) => {
    const url = getPublicUrl(form);
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Forms</h2>
          <p className="text-slate-500">Manage your forms and surveys</p>
        </div>
        <Button onClick={onCreateNew} data-testid="create-form-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search forms..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="form-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="LIVE">Live</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => fetchForms()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Forms Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Responses</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : forms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No forms found</p>
                    <Button variant="outline" className="mt-3" onClick={onCreateNew}>
                      Create Your First Form
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                forms.map((form) => (
                  <TableRow key={form.id} data-testid={`form-row-${form.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">{form.title}</p>
                          {form.description && (
                            <p className="text-sm text-slate-500 line-clamp-1">{form.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[form.status]}>
                        {form.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{form.response_count || 0}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {form.created_at ? new Date(form.created_at).toLocaleDateString() : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`form-actions-${form.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEdit(form.id)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onViewResponses(form.id)}>
                            <Eye className="w-4 h-4 mr-2" /> View Responses
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setLinkDialog({ open: true, form })}>
                            <Link2 className="w-4 h-4 mr-2" /> Get Link
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleClone(form)}>
                            <Copy className="w-4 h-4 mr-2" /> Clone
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {form.status === 'DRAFT' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(form, 'LIVE')}>
                              <Play className="w-4 h-4 mr-2" /> Publish
                            </DropdownMenuItem>
                          )}
                          {form.status === 'LIVE' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(form, 'CLOSED')}>
                              <Pause className="w-4 h-4 mr-2" /> Close
                            </DropdownMenuItem>
                          )}
                          {form.status === 'CLOSED' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(form, 'LIVE')}>
                              <Play className="w-4 h-4 mr-2" /> Reopen
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteDialog({ open: true, form })}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, form: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Form</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.form?.title}"? 
              This will also delete all responses. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, form: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => !open && setLinkDialog({ open: false, form: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Form</DialogTitle>
            <DialogDescription>
              Share this link with respondents to fill out the form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={linkDialog.form ? getPublicUrl(linkDialog.form) : ''} 
                readOnly 
                className="font-mono text-sm"
              />
              <Button onClick={() => linkDialog.form && copyLink(linkDialog.form)}>
                Copy
              </Button>
            </div>
            {linkDialog.form?.status === 'DRAFT' && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ This form is still in draft. Publish it to allow responses.
              </p>
            )}
            {linkDialog.form?.status === 'CLOSED' && (
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                This form is closed and no longer accepting responses.
              </p>
            )}
          </div>
          <DialogFooter>
            {linkDialog.form?.status !== 'LIVE' && (
              <Button onClick={() => window.open(getPublicUrl(linkDialog.form), '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" /> Preview
              </Button>
            )}
            {linkDialog.form?.status === 'LIVE' && (
              <Button onClick={() => window.open(getPublicUrl(linkDialog.form), '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" /> Open Form
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormList;
